/**
 * Wasiyat canonical schema. Single source of truth.
 * Every byte that touches OPFS conforms to these shapes.
 *
 * Design choices:
 *  - Branded IDs prevent cross-table reference mistakes at compile-time.
 *  - All money values: integer paise (₹1 = 100). Avoids float drift.
 *  - All dates: ISO 8601 strings. Sortable as text.
 *  - PII (name, phone, aadhaar) stored hashed where possible.
 *  - Nominee references go through id; nominee record holds private contact.
 */

export const SCHEMA_VERSION = 1 as const;

// ────────────────────── branded ids ──────────────────────
type Brand<K, T> = K & { readonly __brand: T };

export type AssetId = Brand<string, 'AssetId'>;
export type NomineeId = Brand<string, 'NomineeId'>;
export type DocumentId = Brand<string, 'DocumentId'>;
export type SeedId = Brand<string, 'SeedId'>;
export type SubscriptionId = Brand<string, 'SubscriptionId'>;
export type WishId = Brand<string, 'WishId'>;
export type BlobId = Brand<string, 'BlobId'>;

export const newId = <T>(): T => crypto.randomUUID() as unknown as T;

// ────────────────────── domain enums ─────────────────────
export const ASSET_CATEGORIES = [
  'bank_account',
  'mutual_fund',
  'demat',
  'fixed_deposit',
  'crypto',
  'insurance_life',
  'insurance_health',
  'insurance_motor',
  'property',
  'locker',
  'jewellery',
  'business_share',
  'pf_epfo',
  'nps',
  'ppf',
  'digital_domain',
  'digital_hosting',
  'digital_saas',
  'other',
] as const;
export type AssetCategory = (typeof ASSET_CATEGORIES)[number];

export const ASSET_DISPLAY: Record<AssetCategory, { en: string; hi: string; icon: string }> = {
  bank_account: { en: 'Bank account', hi: 'बैंक खाता', icon: 'bank' },
  mutual_fund: { en: 'Mutual fund', hi: 'म्यूचुअल फंड', icon: 'fund' },
  demat: { en: 'Demat / Equity', hi: 'डीमैट / शेयर', icon: 'shares' },
  fixed_deposit: { en: 'Fixed deposit', hi: 'फिक्स्ड डिपॉज़िट', icon: 'fd' },
  crypto: { en: 'Crypto wallet', hi: 'क्रिप्टो वॉलेट', icon: 'crypto' },
  insurance_life: { en: 'Life insurance', hi: 'जीवन बीमा', icon: 'life' },
  insurance_health: { en: 'Health insurance', hi: 'स्वास्थ्य बीमा', icon: 'health' },
  insurance_motor: { en: 'Motor insurance', hi: 'वाहन बीमा', icon: 'motor' },
  property: { en: 'Property', hi: 'संपत्ति', icon: 'home' },
  locker: { en: 'Bank locker', hi: 'बैंक लॉकर', icon: 'locker' },
  jewellery: { en: 'Jewellery', hi: 'जेवरात', icon: 'jewel' },
  business_share: { en: 'Business share', hi: 'व्यवसाय हिस्सा', icon: 'biz' },
  pf_epfo: { en: 'PF / EPFO', hi: 'पीएफ / ईपीएफओ', icon: 'pf' },
  nps: { en: 'NPS', hi: 'एनपीएस', icon: 'nps' },
  ppf: { en: 'PPF', hi: 'पीपीएफ', icon: 'ppf' },
  digital_domain: { en: 'Domain name', hi: 'डोमेन नाम', icon: 'domain' },
  digital_hosting: { en: 'Hosting account', hi: 'होस्टिंग खाता', icon: 'hosting' },
  digital_saas: { en: 'SaaS subscription', hi: 'सब्सक्रिप्शन', icon: 'saas' },
  other: { en: 'Other', hi: 'अन्य', icon: 'other' },
};

export const NOMINEE_RELATIONS = [
  'spouse',
  'child',
  'parent',
  'sibling',
  'in_law',
  'lawyer',
  'executor',
  'trusted_friend',
  'other',
] as const;
export type NomineeRelation = (typeof NOMINEE_RELATIONS)[number];

export const NOMINEE_DISPLAY: Record<NomineeRelation, { en: string; hi: string }> = {
  spouse: { en: 'Spouse', hi: 'जीवनसाथी' },
  child: { en: 'Child', hi: 'संतान' },
  parent: { en: 'Parent', hi: 'माता-पिता' },
  sibling: { en: 'Sibling', hi: 'भाई-बहन' },
  in_law: { en: 'In-law', hi: 'ससुराल' },
  lawyer: { en: 'Lawyer', hi: 'वकील' },
  executor: { en: 'Executor', hi: 'क्रियान्वयक' },
  trusted_friend: { en: 'Trusted friend', hi: 'विश्वसनीय मित्र' },
  other: { en: 'Other', hi: 'अन्य' },
};

export const WISH_CATEGORIES = [
  'funeral',
  'organ_donation',
  'digital_disposal',
  'memorial',
  'pet_care',
  'charity',
  'message',
  'other',
] as const;
export type WishCategory = (typeof WISH_CATEGORIES)[number];

export const DOCUMENT_CATEGORIES = [
  'will',
  'marriage_certificate',
  'birth_certificate',
  'death_certificate_template',
  'property_deed',
  'insurance_policy',
  'aadhaar',
  'pan',
  'passport',
  'driving_licence',
  'medical_record',
  'business_doc',
  'other',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const SUBSCRIPTION_CATEGORIES = [
  'media',
  'cloud',
  'domain',
  'hosting',
  'productivity',
  'fitness',
  'news',
  'gaming',
  'utility',
  'other',
] as const;
export type SubscriptionCategory = (typeof SUBSCRIPTION_CATEGORIES)[number];

export const FAMILY_IMPACT = ['critical', 'inconvenient', 'none'] as const;
export type FamilyImpact = (typeof FAMILY_IMPACT)[number];

export const CHAINS = ['bitcoin', 'ethereum', 'solana', 'polygon', 'tron', 'cardano', 'other'] as const;
export type Chain = (typeof CHAINS)[number];

export const WISH_PRIORITIES = ['must', 'preferred', 'nice_to_have'] as const;
export type WishPriority = (typeof WISH_PRIORITIES)[number];

// ────────────────────── records ──────────────────────────
export interface Asset {
  id: AssetId;
  category: AssetCategory;
  institution: string; // e.g., "HDFC Bank", "Zerodha"
  identifier_last4: string; // last 4 of account / folio / policy no.
  estimated_value_paise: number; // integer paise
  custodian_notes: string; // where to find proof
  nominee_ids: NomineeId[];
  document_ids: DocumentId[];
  created_at: string;
  updated_at: string;
}

export interface Nominee {
  id: NomineeId;
  relation: NomineeRelation;
  display_name: string; // first name + relation suffix
  aadhaar_last4: string; // 4 chars or empty
  phone_hash: string; // SHA-256(phone) or empty
  digilocker_id: string; // user-supplied or empty
  contact_priority: 1 | 2 | 3 | 4 | 5;
  is_shamir_holder: boolean; // receives a Shamir share on death-trigger
  notes: string; // encrypted at rest with same key
  created_at: string;
}

export interface LastWish {
  id: WishId;
  category: WishCategory;
  instruction: string;
  priority: WishPriority;
  audience_nominee_ids: NomineeId[]; // who should see this on release
  created_at: string;
}

export interface DocumentEntry {
  id: DocumentId;
  category: DocumentCategory;
  title: string;
  blob_id: BlobId | null; // OPFS file ref, null if metadata-only
  size_kb: number;
  mime_type: string;
  notes: string;
  password_protected: boolean;
  created_at: string;
}

export interface CryptoSeed {
  id: SeedId;
  wallet_name: string;
  chain: Chain;
  shamir_share_count: 5;
  shamir_threshold: 3;
  shamir_shares_encrypted: string[]; // 5 base64 strings, each AES-GCM with nominee public hint
  hint: string;
  estimated_value_paise: number;
  created_at: string;
}

export interface Subscription {
  id: SubscriptionId;
  service: string;
  monthly_paise: number;
  billing_email_hash: string;
  cancellation_url: string;
  category: SubscriptionCategory;
  family_impact: FamilyImpact;
  notes: string;
  created_at: string;
}

export interface DeadManSwitch {
  enabled: boolean;
  ping_interval_days: 30 | 60 | 90 | 180 | 365;
  last_ping_at: string;
  grace_period_days: number;
  secondary_verifier_id: NomineeId | null;
  shamir_threshold: 3;
  shamir_total: 5;
  release_status: 'idle' | 'pending_ping' | 'overdue' | 'verifying' | 'released';
}

export interface Readiness {
  score: number; // 0..100
  breakdown: {
    assets: number;
    nominees: number;
    documents: number;
    wishes: number;
    dms: number;
    crypto: number;
    subs: number;
  };
  next_action: string;
}

export interface VaultOwner {
  display_name: string; // first name only by default
  locale: 'en-IN' | 'hi-IN';
  pronouns: 'he' | 'she' | 'they';
  birth_year: number;
}

export interface Vault {
  schema_version: typeof SCHEMA_VERSION;
  generated_at: string;
  owner: VaultOwner;
  assets: Asset[];
  nominees: Nominee[];
  last_wishes: LastWish[];
  documents: DocumentEntry[];
  crypto_seeds: CryptoSeed[];
  subscriptions: Subscription[];
  dead_man_switch: DeadManSwitch;
  readiness: Readiness;
}

// ────────────────────── helpers ──────────────────────────
export const fromPaise = (paise: number): number => paise / 100;
export const toPaise = (rupees: number): number => Math.round(rupees * 100);

export const formatINR = (paise: number): string => {
  const rupees = paise / 100;
  const rounded = Math.round(rupees);
  if (rounded < 1000) return `₹${rounded.toLocaleString('en-IN')}`;
  if (rounded < 100_000) return `₹${rounded.toLocaleString('en-IN')}`;
  if (rounded < 10_000_000) {
    const lakhs = rounded / 100_000;
    return `₹${lakhs.toFixed(lakhs >= 10 ? 1 : 2)}L`;
  }
  const crore = rounded / 10_000_000;
  return `₹${crore.toFixed(crore >= 10 ? 1 : 2)}Cr`;
};

export const todayISO = (): string => new Date().toISOString();

export function emptyVault(owner: VaultOwner): Vault {
  return {
    schema_version: SCHEMA_VERSION,
    generated_at: todayISO(),
    owner,
    assets: [],
    nominees: [],
    last_wishes: [],
    documents: [],
    crypto_seeds: [],
    subscriptions: [],
    dead_man_switch: {
      enabled: false,
      ping_interval_days: 90,
      last_ping_at: todayISO(),
      grace_period_days: 30,
      secondary_verifier_id: null,
      shamir_threshold: 3,
      shamir_total: 5,
      release_status: 'idle',
    },
    readiness: {
      score: 0,
      breakdown: { assets: 0, nominees: 0, documents: 0, wishes: 0, dms: 0, crypto: 0, subs: 0 },
      next_action: 'Add your first nominee',
    },
  };
}

export function computeReadiness(v: Vault): Readiness {
  // Each axis is 0..100, then weighted-average to overall score.
  const assets = clamp(v.assets.length * 10, 0, 100);
  const nominees = clamp(v.nominees.length * 25, 0, 100);
  const documents = clamp(v.documents.length * 15, 0, 100);
  const wishes = clamp(v.last_wishes.length * 20, 0, 100);
  const dms = v.dead_man_switch.enabled ? 100 : 0;
  const crypto = v.crypto_seeds.length === 0 ? 100 : clamp(v.crypto_seeds.filter((s) => s.shamir_shares_encrypted.length === 5).length / v.crypto_seeds.length * 100, 0, 100);
  const subs = clamp(v.subscriptions.length * 12, 0, 100);

  const weights = { assets: 0.25, nominees: 0.20, documents: 0.15, wishes: 0.10, dms: 0.15, crypto: 0.10, subs: 0.05 };
  const score = Math.round(
    assets * weights.assets +
    nominees * weights.nominees +
    documents * weights.documents +
    wishes * weights.wishes +
    dms * weights.dms +
    crypto * weights.crypto +
    subs * weights.subs
  );

  let next_action = 'Vault is ready. Re-verify every 90 days.';
  if (v.nominees.length === 0) next_action = 'Add your first nominee';
  else if (v.assets.length === 0) next_action = 'Log your most important asset';
  else if (v.documents.length === 0) next_action = 'Upload your will or marriage certificate';
  else if (v.last_wishes.length === 0) next_action = 'Record your last wishes';
  else if (!v.dead_man_switch.enabled) next_action = 'Activate the dead-man-switch';

  return { score, breakdown: { assets, nominees, documents, wishes, dms, crypto, subs }, next_action };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function totalAssetValue(v: Vault): number {
  return v.assets.reduce((s, a) => s + a.estimated_value_paise, 0);
}

export function monthlySubsTotal(v: Vault): number {
  return v.subscriptions.reduce((s, x) => s + x.monthly_paise, 0);
}

export function criticalSubsCount(v: Vault): number {
  return v.subscriptions.filter((s) => s.family_impact === 'critical').length;
}
